import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, Inbox, MessageSquare, Star, ArrowRight, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  screening: "bg-amber-100 text-amber-700",
  shortlisted: "bg-purple-100 text-purple-700",
  interview: "bg-indigo-100 text-indigo-700",
  offered: "bg-emerald-100 text-emerald-700",
  hired: "bg-green-100 text-green-800",
  rejected: "bg-rose-100 text-rose-700",
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;

  const kpis = [
    { label: "Active jobs", value: d.jobs.active, sub: `${d.jobs.total} total`, icon: Briefcase, to: "/admin/jobs" },
    { label: "Applications", value: d.applications.total, sub: `${d.applications.new} new`, icon: Users, to: "/admin/applications" },
    { label: "Enquiries", value: d.enquiries.total, sub: `${d.enquiries.unread} unread`, icon: Inbox, to: "/admin/enquiries" },
    { label: "Feedback", value: d.feedback.total, sub: "All time", icon: MessageSquare, to: "/admin/feedback" },
    { label: "Pending testimonials", value: d.testimonials.pending, sub: "Need approval", icon: Star, to: "/admin/testimonials" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">An overview of activity across the platform.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map(({ label, value, sub, icon: Icon, to }) => (
          <Link key={label} to={to} className="block">
            <Card className="transition-all hover:-translate-y-0.5 hover:shadow-md">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</div>
                    <div className="mt-1 text-xs text-slate-500">{sub}</div>
                  </div>
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between border-b pb-3">
          <CardTitle className="text-base">Applications pipeline</CardTitle>
          <Link to="/admin/pipeline" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">Open Kanban <ArrowRight className="h-4 w-4" /></Link>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid gap-3 sm:grid-cols-7">
            {Object.entries(d.applications.pipeline).map(([s, n]) => (
              <div key={s} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                <Badge className={`mb-2 ${STATUS_COLORS[s] || "bg-slate-200"}`}>{s}</Badge>
                <div className="font-heading text-2xl font-bold text-slate-900">{n}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="border-b pb-3"><CardTitle className="text-base">Recent applications</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-2">Candidate</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Status</th></tr>
              </thead>
              <tbody>
                {d.recent_applications.map((a) => (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-900">{a.candidate_name}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{a.job_title || "—"}</td>
                    <td className="px-4 py-2"><Badge className={STATUS_COLORS[a.status] || "bg-slate-200"}>{a.status}</Badge></td>
                  </tr>
                ))}
                {d.recent_applications.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">No applications yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="border-b pb-3"><CardTitle className="text-base">Recent enquiries</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y">
              {d.recent_enquiries.map((e) => (
                <li key={e.id} className="px-4 py-3 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-900">{e.name}</div>
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">{e.type}</Badge>
                  </div>
                  <div className="text-xs text-slate-500">{e.email || e.phone}</div>
                  <div className="mt-1 line-clamp-1 text-sm text-slate-600">{e.message || e.subject || "(no message)"}</div>
                </li>
              ))}
              {d.recent_enquiries.length === 0 && (
                <li className="px-4 py-6 text-center text-slate-400">No enquiries yet.</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
